import { FC } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import { cellBackground } from "@/lib/scoring/matrix/bands";
import type { MatrixCell as MatrixCellData } from "@/lib/scoring/matrix/types";
import { formatNumber } from "@/utils/numberUtils";

interface MatrixCellProps {
  cell: MatrixCellData;
  onClick: () => void;
  loading?: boolean;
}

export const MatrixCell: FC<MatrixCellProps> = ({
  cell,
  onClick,
  loading = false,
}) => {
  const moreCount = Math.max(0, cell.count - cell.sampleNames.length);
  const interactive = !loading && cell.count > 0;

  return (
    <Box
      role="button"
      tabIndex={interactive ? 0 : -1}
      data-testid={`matrix-cell-${cell.fit}-${cell.intent}`}
      onClick={interactive ? onClick : undefined}
      onKeyDown={(e) => {
        if (interactive && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        position: "relative",
        bgcolor: cellBackground(cell.fit, cell.intent),
        borderRadius: 1.5,
        p: { xs: 1, md: 2 },
        color: "rgba(0,0,0,0.82)",
        overflow: "hidden",
        cursor: interactive ? "pointer" : "default",
        transition: "box-shadow 150ms, transform 150ms",
        "&:hover": interactive
          ? { boxShadow: 3, transform: "translateY(-1px)" }
          : undefined,
      }}
    >
      <Typography
        sx={{
          position: "absolute",
          top: { xs: 6, md: 10 },
          right: { xs: 8, md: 12 },
          fontSize: { xs: 8, md: 10 },
          fontWeight: 700,
          letterSpacing: "0.5px",
          textTransform: "uppercase",
          color: "rgba(0,0,0,0.45)",
        }}
      >
        {cell.actionLabel}
      </Typography>

      {loading ? (
        <Skeleton variant="text" width={64} height={40} />
      ) : (
        <Typography
          fontWeight={700}
          sx={{
            lineHeight: 1,
            fontSize: { xs: "1.1rem", sm: "1.5rem", md: "2.125rem" },
          }}
        >
          {formatNumber(cell.count)}
        </Typography>
      )}

      {/* Sample names are hidden on the smallest screens to keep cells compact. */}
      <Stack
        spacing={0.25}
        mt={{ xs: 0.5, md: 1.5 }}
        sx={{ display: { xs: "none", sm: "block" } }}
      >
        {loading &&
          [0, 1, 2].map((i) => <Skeleton key={i} variant="text" width="70%" />)}
        {!loading &&
          cell.sampleNames.map((name, i) => (
            <Typography
              key={`${name}-${i}`}
              variant="body2"
              noWrap
              sx={{ color: "rgba(0,0,0,0.7)" }}
            >
              {name}
            </Typography>
          ))}
        {moreCount > 0 && (
          <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.5)" }}>
            +{formatNumber(moreCount)} more
          </Typography>
        )}
      </Stack>
    </Box>
  );
};
